import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { validateSection } from '../validators';
import calculateCompletion from '../utils/completion';
import { useHistory } from './HistoryContext';
import { migrateResume } from '../../../core/schema/migrateResume';

const ResumeContext = createContext(null);

/**
 * ResumeProvider — single source of truth for the form editor.
 *
 * Sync rules (to prevent Editor ↔ Preview fighting):
 *
 *  1. When the editor makes a change:
 *     - `updateResumeState` sets `isInternalUpdateRef = true`
 *     - Calls `onUpdateParent(migrated)` → `setResumeData` in AppContext
 *     - AppContext updates → `initialResume` prop changes
 *     - Sync effect fires but sees `isInternalUpdateRef = true` → skips override ✅
 *
 *  2. When the AI makes a change (external):
 *     - `initialResume` prop changes (different resumeId or updated content)
 *     - Sync effect sees `isInternalUpdateRef = false` → accepts update ✅
 *     - Editor and preview both reflect the AI's output immediately ✅
 *
 *  3. When switching documents:
 *     - `resumeId` changes → always accept new document ✅
 */
export function ResumeProvider({ children, initialResume, onUpdateParent }) {
  const [activeResume, setActiveResumeState] = useState(() => migrateResume(initialResume));
  const [isDirty, setIsDirty] = useState(false);
  const [validationResults, setValidationResults] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);

  const { pushState } = useHistory();

  // Ref to suppress echo-back: set true when we push to parent, cleared after one render cycle.
  const isInternalUpdateRef = useRef(false);
  // Track last seen resumeId to detect document switches.
  const lastResumeIdRef = useRef(initialResume?.id || 'master');

  // Run validators whenever activeResume changes
  useEffect(() => {
    if (!activeResume) return;

    const results = {};
    const activeIds = activeResume.sections || ['contact', 'summary', 'experience', 'education', 'skills', 'projects'];

    activeIds.forEach(id => {
      let data = activeResume;
      if (id === 'contact') {
        data = {
          name: activeResume.name,
          email: activeResume.email,
          phone: activeResume.phone,
          role: activeResume.role,
          city: activeResume.city,
        };
      } else {
        data = activeResume[id];
      }
      results[id] = validateSection(id, data);
    });

    setValidationResults(results);
    setCompletionPercentage(calculateCompletion(results));
  }, [activeResume]);

  // Sync initialResume → editor when:
  //   (a) the document switches (resumeId change), OR
  //   (b) an external update arrives (AI, other client) — but NOT our own echo-backs.
  //
  // IMPORTANT: `activeResume` is intentionally NOT in the dependency array.
  // It was previously included which caused an infinite loop:
  //   editor edit → onUpdateParent → initialResume changes → effect → setActiveResumeState
  //   → activeResume changes → effect again → etc.
  useEffect(() => {
    if (!initialResume) return;

    const resumeId = initialResume.id || 'master';
    const isDocSwitch = lastResumeIdRef.current !== resumeId;

    if (isDocSwitch) {
      // Always accept a document switch regardless of origin
      lastResumeIdRef.current = resumeId;
      isInternalUpdateRef.current = false;
      setActiveResumeState(migrateResume(initialResume));
      setIsDirty(false);
      return;
    }

    // Same document — only sync if this was NOT triggered by our own edit
    if (isInternalUpdateRef.current) {
      // This initialResume change came from us (echo-back). Clear the flag and skip.
      isInternalUpdateRef.current = false;
      return;
    }

    // External update (AI chat / other client) → accept it
    console.log('[ResumeContext] External update detected (AI/other). Syncing editor.');
    setActiveResumeState(migrateResume(initialResume));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResume]);

  const updateResumeState = useCallback((newResume, pushToHistory = true) => {
    const migrated = migrateResume(newResume);
    if (pushToHistory && activeResume) {
      pushState(JSON.parse(JSON.stringify(activeResume)));
    }
    // Mark this change as internal BEFORE calling setActiveResumeState so the
    // sync effect (which will fire on the resulting initialResume prop update) skips it.
    isInternalUpdateRef.current = true;
    setActiveResumeState(migrated);
    setIsDirty(true);
    if (onUpdateParent) {
      onUpdateParent(migrated);
    }
  }, [activeResume, pushState, onUpdateParent]);

  const updateField = useCallback((path, value) => {
    const parts = path.split('.');
    const nextResume = JSON.parse(JSON.stringify(activeResume || {}));

    let current = nextResume;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const nextPartIsNumber = !isNaN(Number(parts[i + 1]));
      if (current[part] === undefined) {
        current[part] = nextPartIsNumber ? [] : {};
      }
      current = current[part];
    }
    current[parts[parts.length - 1]] = value;

    updateResumeState(nextResume);
  }, [activeResume, updateResumeState]);

  const value = {
    activeResume,
    isDirty,
    setIsDirty,
    validationResults,
    completionPercentage,
    updateResumeState,
    updateField,
  };

  return (
    <ResumeContext.Provider value={value}>
      {children}
    </ResumeContext.Provider>
  );
}

export const useResume = () => {
  const ctx = useContext(ResumeContext);
  if (!ctx) throw new Error('useResume must be used within a ResumeProvider');
  return ctx;
};
